import { useDispatch, useSelector } from 'react-redux';

import {
  getDynamicFormScaleSelector,
  getDynamicFormSelector,
  getDynamicFormSignatureConfigSelector,
} from 'store/selectors/requestFormProcess';
import { FormDocumentQuestionType, FormDocumentQuestionsType } from 'types';
import { DYNAMIC_QUESTION_TYPE, getDynamicFormColor } from 'app-constants';
import {
  updateDynamicFormActiveQuestionEffect,
  updateDynamicFormHoveredQuestionEffect,
} from 'store/effects/formProcess';
import { SignatureField, TextField } from './Fields';
import { getCohesiveFieldAnswers } from './Fields/helper';
import { textFieldsType, signatureFieldType } from './Fields/types';
import { CommentTooltip } from './FormComments/CommentTooltip';

import { useManageFormComments } from './FormComments/hooks/useManageFormComments';
import { dynamicManager } from 'pages/FormProcess/DynamicForm/DynamicManager';

interface FieldRendererProps {
  question: FormDocumentQuestionType;
  disabled?: boolean;
  isCohesiveFlow?: boolean;
  otherSignatoryField?: boolean;
  documentIndex?: number;
  pageQuestions?: FormDocumentQuestionsType;
}

export const FieldRenderer = ({
  question,
  disabled,
  documentIndex = -1,
  isCohesiveFlow,
  otherSignatoryField,
  pageQuestions,
}: FieldRendererProps) => {
  const dispatch = useDispatch();

  const fieldDisabled = disabled || question.disabled;

  const {
    selectedFieldUUID: fieldUUID,
    responses: answers,
    allFormRoles,
    hideQuestionRoles,
    activeQuestionUUID,
  } = useSelector(getDynamicFormSignatureConfigSelector);

  const { bundleDocumentsDetails = [], dynamicFormDocuments = [] } =
    useSelector(getDynamicFormSelector);

  const scale = useSelector(getDynamicFormScaleSelector);

  const { formsComments } = useManageFormComments();

  let responses = isCohesiveFlow ? getCohesiveFieldAnswers(bundleDocumentsDetails) : answers;

  let selectedFieldUUID = activeQuestionUUID || fieldUUID;

  const isHighlighted = question.UUID === selectedFieldUUID;

  const answer = responses[question.UUID]?.Answer || '';

  const field = question?.Fields?.[0];
  const widget = field?.widgets?.[0];

  const { x, y, width, height } = widget;

  const color = getDynamicFormColor(allFormRoles, question);

  if (!widget) return <></>;

  const isSignatureField = signatureFieldType.includes(question.Type as DYNAMIC_QUESTION_TYPE);

  const handleFieldClick = (e) => {
    if (disabled) return;

    onMouseEnter();
    if (!isHighlighted) {
      dispatch(
        updateDynamicFormActiveQuestionEffect({
          uuid: question.UUID,
          documentIndex,
          isCohesiveFlow,
        }),
      );
    }
  };

  const onMouseEnter = () => {
    setTimeout(
      () => dispatch(updateDynamicFormHoveredQuestionEffect({ hoveredFieldUUID: question.UUID })),
      100,
    );
  };

  const onMouseLeave = () => {
    setTimeout(
      () => dispatch(updateDynamicFormHoveredQuestionEffect({ hoveredFieldUUID: '' })),
      100,
    );
  };

  const renderField = (disableOffset?, existingComment?) => {
    const questionType = question.Type as DYNAMIC_QUESTION_TYPE;
    const mouseEventProps = {
      ...(existingComment && { onMouseEnter, onMouseLeave }),
    };

    if (textFieldsType.includes(questionType)) {
      return (
        <TextField
          question={question}
          answer={answer}
          color={color}
          disabled={fieldDisabled}
          isHighlighted={isHighlighted}
          handleFieldClick={handleFieldClick}
          documentIndex={documentIndex}
          isCohesiveFlow={isCohesiveFlow}
          otherSignatoryField={otherSignatoryField}
          pageQuestions={pageQuestions}
          responses={responses}
          disableOffset={disableOffset}
          {...mouseEventProps}
        />
      );
    } else if (isSignatureField) {
      return (
        <SignatureField
          question={question}
          answer={answer}
          color={color}
          disabled={fieldDisabled || otherSignatoryField}
          isOtherSignatory={otherSignatoryField}
          isHighlighted={isHighlighted}
          documentIndex={documentIndex}
          isCohesiveFlow={isCohesiveFlow}
          disableOffset={disableOffset}
          onFieldClick={handleFieldClick}
          {...mouseEventProps}
          enableClick={disableOffset || existingComment}
        />
      );
    }

    return <></>;
  };

  const getUnresolvedComments = () => {
    return formsComments[question?.UUID].filter(
      (thread) => !thread.Resolved && thread.Comments.length,
    );
  };

  if (
    question?.CustomPermissions?.[0] &&
    hideQuestionRoles?.includes(question?.CustomPermissions?.[0])
  ) {
    return <></>;
  }

  if (
    fieldDisabled ||
    (isSignatureField && otherSignatoryField) ||
    !dynamicManager.canShowCommentsList()
  )
    return renderField();

  if (formsComments[question?.UUID] && getUnresolvedComments().length)
    return renderField(false, true);

  const commentProps = {
    selectedFieldUUID,
    isHighlighted,
    answer,
    field: {
      ...field,
      meta: {
        fieldId: question.Type,
        questionUUID: question?.UUID,
        coordinates: {
          x,
          y,
          height,
        },
        page: widget.pageNumber,
        formProcessPublicId: dynamicManager.getFormProcessPublicId(),
        formDocumentPublicId: dynamicFormDocuments[documentIndex]?.PublicId,
      },
      widget,
      documentIndex,
    },
  };

  return (
    <CommentTooltip {...commentProps} scale={scale} width={width} manageOffset={isSignatureField}>
      {renderField(isSignatureField)}
    </CommentTooltip>
  );
};
